import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Gradients } from "components/gradients";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import {
  DEFAULT_TICK_COLOR,
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 30;
const END_ANGLE = 330;
const MAX_VALUE = 60;
const DIAMETER = 450;
const ARC_WIDTH = 22;
const TICK_OFFSET = ARC_WIDTH + 10;
const TICK_WIDTH = 4;
const TICK_LENGTH = 15;
const STROKE_CAP = "round";
const MIN_SOG_TO_SHOW = 1;

export const SpeedGauge = () => {
  const { t } = useTranslation();

  // Store values
  const sog = useStore((state) => state.sog);

  // Animated values for smooth transitions
  const animatedSog = useAnimatedValue(sog);

  // Gauge setup
  const gauge = useGauge({
    domain: [0, MAX_VALUE],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: MAX_VALUE,
    diameter: DIAMETER,
  });

  // Determine tick color based on current SOG
  const getTickColor = useCallback(
    (tickValue: number, defaultColor: string) => {
      const tickInSogRange = sog !== null && tickValue <= sog;

      // In SOG range
      if (tickInSogRange) return Color.Indigo[400];

      return defaultColor;
    },
    [sog]
  );

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 1.5)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle:
              animatedSog > MAX_VALUE
                ? gauge.valueToAngle(MAX_VALUE)
                : gauge.valueToAngle(
                    animatedSog > MIN_SOG_TO_SHOW ? animatedSog : 0
                  ),
          })}
          fill="none"
          stroke={`url(#${SvgGradients.Blue})`}
          strokeWidth={sog !== null && sog > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.55}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.5}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Ticks */}
      <g id="ticks">
        {gauge.ticks.map((angle, index) => {
          const asValue = gauge.angleToValue(angle);
          const showText = asValue % 5 === 0;
          const tickColor = getTickColor(asValue, DEFAULT_TICK_COLOR);

          return (
            <Fragment key={`tick-${angle}-${index}`}>
              <line
                strokeWidth={TICK_WIDTH}
                stroke={tickColor}
                strokeLinecap={"round"}
                {...gauge.getTickProps({
                  angle,
                  length: showText ? TICK_LENGTH : TICK_LENGTH * 0.5,
                  offset: -TICK_OFFSET,
                })}
              />
              {showText && (
                <text
                  className="text-base font-medium font-mono"
                  fill={tickColor}
                  {...gauge.getLabelProps({ angle, offset: TICK_OFFSET * 1.9 })}
                >
                  {asValue}
                </text>
              )}
            </Fragment>
          );
        })}
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          y={"15%"}
          className="text-xl font-semibold uppercase"
          fill={Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {t("dashboard.sog")}
        </text>
        <text
          fill={Color.White}
          className="text-9xl font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {sog !== null ? Math.round(sog) : PLACEHOLDER}
        </text>
      </g>
    </svg>
  );
};
